import {
  buildParameterValue,
  type ParameterValue,
} from '@robovision/data'
import type {
  HubAlgorithmDefinition,
  HubDeploymentDefinition,
  HubJobDefinition,
} from '@robovision/hub'
import {
  buildObject,
  distinct,
} from '@robovision/rvai-utils'
import type { JobRuntimeParameters } from './common/JobRuntimeParameters'
import { JobBlobDependency } from './dependencies/JobBlobDependency'
import { JobDatapoolDependency } from './dependencies/JobDatapoolDependency'
import { JobDatasetDependency } from './dependencies/JobDatasetDependency'
import { JobExampleStreamsDependency } from './dependencies/JobExampleStreamsDependency'
import { JobModelDependency } from './dependencies/JobModelDependency'
import { JobParameterDependency } from './dependencies/JobParameterDependency'
import { JobDatasetDefinition } from './JobDatasetDefinition'


type JobDefinition = HubJobDefinition | HubDeploymentDefinition

export abstract class BaseDefinition<TJob extends JobDefinition = JobDefinition> {
  public readonly blobDependencies: Record<string, JobBlobDependency>
  public readonly datapoolDependencies: Record<string, JobDatapoolDependency>
  // TODO this can be refactored into the JobDatasetDependency class
  public readonly datasetDefinitions: JobDatasetDefinition[]
  public readonly datasetDependencies: Record<string, JobDatasetDependency>
  public readonly exampleStreamsDependencies: Record<string, JobExampleStreamsDependency>
  public readonly job: TJob
  public readonly modelDependencies: Record<string, JobModelDependency>
  public readonly parametersDependencies: Record<string, JobParameterDependency>

  public constructor (
    jobDefinition: TJob,
    algorithmDefinition: HubAlgorithmDefinition,
  ) {
    this.job = jobDefinition

    this.blobDependencies = this.initBlobDependencies(algorithmDefinition)
    this.datapoolDependencies = this.initDatapoolDependencies(algorithmDefinition)
    this.datasetDefinitions = this.initDatasetDefinitions(algorithmDefinition)
    this.datasetDependencies = this.initDatasetDependencies(algorithmDefinition)
    this.exampleStreamsDependencies = this.initExampleStreamsDependencies(algorithmDefinition)
    this.modelDependencies = this.initModelDependencies()
    this.parametersDependencies = this.initParametersDependencies(algorithmDefinition)
  }

  public buildDefaultParameters (): Record<string, Record<string, ParameterValue>> {
    const definitions = Object.entries(this.parametersDependencies)
    const parameters: Record<string, Record<string, ParameterValue>> = {}

    for (const [dependencyId, definition] of definitions) {
      if (!definition) continue

      for (const field of definition.fields) {
        if ('default' in field && field.default != null) {
          const value = buildParameterValue(field.type, field.default as never) // TODO we need better types still
          parameters[dependencyId] = {
            ...parameters[dependencyId],
            [field.key]: value,
          }
        }
      }
    }

    return parameters
  }

  public abstract buildDefaultResources (): JobRuntimeParameters

  public get description (): string | undefined {
    return this.job.description
  }

  public get id (): string {
    return this.job.id
  }

  public get name (): string {
    return this.job.name
  }

  private initBlobDependencies (algorithmDefinition: HubAlgorithmDefinition) {
    return buildObject(
      this.job.dependencies?.blobs ?? [],
      dep => dep.id,
      dep => JobBlobDependency.fromDefinition(dep, algorithmDefinition),
    )
  }

  private initDatapoolDependencies (algorithmDefinition: HubAlgorithmDefinition) {
    return buildObject(
      this.job.dependencies?.datapools ?? [],
      dep => dep.id,
      dep => JobDatapoolDependency.fromDefinition(dep, algorithmDefinition),
    )
  }

  private initDatasetDependencies (algorithmDefinition: HubAlgorithmDefinition) {
    return buildObject(
      this.job.dependencies?.datasets ?? [],
      dep => dep.id,
      dep => JobDatasetDependency.fromDefinition(dep, algorithmDefinition),
    )
  }

  private initExampleStreamsDependencies (algorithmDefinition: HubAlgorithmDefinition) {
    return buildObject(
      this.job.dependencies?.exampleStreams ?? [],
      dep => dep.id,
      dep => JobExampleStreamsDependency.fromDefinition(dep, algorithmDefinition),
    )
  }

  private initModelDependencies () {
    return buildObject(
      this.job.dependencies?.models ?? [],
      dep => dep.id,
      dep => JobModelDependency.fromDefinition(dep),
    )
  }

  private initParametersDependencies (algorithmDefinition: HubAlgorithmDefinition) {
    return buildObject(
      this.job.dependencies?.parameters ?? [],
      (dep) => dep.id,
      (dep) => JobParameterDependency.fromDefinition(dep, algorithmDefinition),
    )
  }

  private initDatasetDefinitions (algorithmDefinition: HubAlgorithmDefinition) {
    const datasetDefinitionIds = distinct(
      (this.job.dependencies?.datasets ?? []).map(dep => dep.datasetDefinition),
    )
    const datasetDefinitions = datasetDefinitionIds
      .map(id => algorithmDefinition.datasetDefinitions?.find(def => def.id === id))
      .filter(Boolean)

    return datasetDefinitions.map(def =>
      JobDatasetDefinition.fromDefinition(def, this.job, algorithmDefinition))
  }

  /**
   * @deprecated
   * Use `parametersDependencies` instead
   */
  public get parametersDefinitions (): Record<string, JobParameterDependency> {
    return this.parametersDependencies
  }
}
